import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import "../../styles/home.css";
import "./account-deletion.css";
import { accountDeletionTranslations } from "./content";

const supportEmail = "info@luckyspingames.com";

function AccountDeletion({ gameName, images }) {
  const lang = "en";
  const content = accountDeletionTranslations[lang];
  const year = useMemo(() => new Date().getFullYear(), []);

  useEffect(() => {
    document.documentElement.lang = lang;
    document.title = `${gameName} — ${content.pageTitle} | Lucky Spin Games`;
  }, [content.pageTitle, gameName, lang]);

  return (
    <div className="lsg-home account-deletion-page">
      <nav className="lsg-nav">
        <div className="lsg-container lsg-nav-inner">
          <Link to="/" className="lsg-nav-logo" aria-label="Lucky Spin Games home">
            <img className="lsg-logo-white" src={`${process.env.PUBLIC_URL}/lsg/brand/lsg-horizontal-dark.svg`} alt="Lucky Spin Games" />
          </Link>

          <ul className="lsg-nav-links">
            <li><a href="/#games">{content.nav.games}</a></li>
            <li><a href="/#about">{content.nav.about}</a></li>
            <li><a href="/#support">{content.nav.contact}</a></li>
          </ul>
        </div>
      </nav>

      <main className="account-deletion-shell">
        <div className="lsg-container">
          <section className="account-deletion-hero reveal visible">
            <p className="account-deletion-eyebrow">{gameName}</p>
            <h1>{content.title}</h1>
            <p>{content.hero(gameName)}</p>
          </section>

          <section className="account-deletion-feature-card reveal visible">
            <div className="account-deletion-image account-deletion-image-top">
              <img src={images.top} alt={gameName} />
            </div>
            <div className="account-deletion-feature-copy">
              <span className="account-deletion-badge">{content.badge}</span>
              <h2>{content.optionsTitle}</h2>
              <p>{content.optionsIntro(gameName)}</p>
              <p>{content.optionsBody}</p>
            </div>
          </section>

          <section className="account-deletion-grid">
            <article className="account-deletion-card reveal visible">
              <span className="account-deletion-card-icon">@</span>
              <h2>{content.contactTitle}</h2>
              <p>{content.contactBeforeEmail}<a href={`mailto:${supportEmail}`}>{supportEmail}</a>{content.contactAfterEmail}</p>
              <p>{content.contactVerification}</p>
              <p>{content.contactCancellation}</p>
            </article>

            <article className="account-deletion-card reveal visible">
              <span className="account-deletion-card-icon">30</span>
              <h2>{content.retentionTitle}</h2>
              {content.retention.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
            </article>
          </section>

          <section className="account-deletion-card account-deletion-steps reveal visible">
            <div className="account-deletion-section-heading">
              <span>{content.flowLabel}</span>
              <h2>{content.flowTitle}</h2>
              <p>{content.flowIntro(gameName)}</p>
            </div>

            <ol className="account-deletion-step-list">
              <li>
                <div className="account-deletion-step-number">01</div>
                <div><h3>{content.steps[0].title}</h3><p>{content.steps[0].body(gameName)}</p></div>
              </li>
              <li>
                <div className="account-deletion-step-number">02</div>
                <div>
                  <h3>{content.steps[1].title}</h3>
                  <p>{content.steps[1].body}</p>
                  <figure className="account-deletion-image"><img src={images.gear} alt={`${gameName} settings`} /></figure>
                </div>
              </li>
              <li>
                <div className="account-deletion-step-number">03</div>
                <div>
                  <h3>{content.steps[2].title}</h3>
                  <p>{content.steps[2].body}</p>
                  <figure className="account-deletion-image"><img src={images.delete} alt={`${gameName} delete account screen`} /></figure>
                </div>
              </li>
              <li>
                <div className="account-deletion-step-number">04</div>
                <div><h3>{content.steps[3].title}</h3><p>{content.steps[3].body}</p></div>
              </li>
            </ol>
          </section>

          <section className="account-deletion-contact reveal visible">
            <span className="account-deletion-card-icon">?</span>
            <h2>{content.questionsTitle}</h2>
            <p>{content.questionsBody}</p>
            <a className="account-deletion-button" href={`mailto:${supportEmail}`}>{content.supportLabel}</a>
          </section>
        </div>
      </main>

      <footer className="lsg-footer">
        <div className="lsg-container">
          <div className="lsg-footer-grid">
            <div>
              <div className="lsg-footer-brand-row">
                <img className="lsg-logo-white" style={{ width: 120, height: "auto", borderRadius: 0 }} src={`${process.env.PUBLIC_URL}/lsg/brand/lsg-stacked-dark.svg`} alt="" />
              </div>
              <p className="lsg-footer-tagline">{content.footer.tagline}</p>
              <p className="lsg-footer-blurb">{content.footer.blurb}</p>
            </div>

            <div className="lsg-footer-col">
              <p className="lsg-footer-col-title">{content.footer.games}</p>
              <a href="/#games">Merge Treasure Hunt</a>
              <a href="/#games">Rare Find</a>
            </div>

            <div className="lsg-footer-col">
              <p className="lsg-footer-col-title">{content.footer.company}</p>
              <a href="/#about">{content.footer.about}</a>
              <a href="/#support">{content.footer.contact}</a>
            </div>

            <div className="lsg-footer-col">
              <p className="lsg-footer-col-title">{content.footer.getInTouch}</p>
              <a href="mailto:hello@luckyspingames.com">hello@luckyspingames.com</a>
              <a href="https://www.luckyspingames.com" target="_blank" rel="noopener noreferrer">luckyspingames.com</a>
              <Link to="/privacy-policy">{content.footer.privacy}</Link>
              <Link to="/terms">{content.footer.terms}</Link>
            </div>
          </div>

          <div className="lsg-footer-bottom">
            {content.footer.copy(year)}
          </div>
        </div>
      </footer>
    </div>
  );
}

export default AccountDeletion;
